/**
 * HistoryChartCard — the framed trend panel on the hospital-detail screen: a
 * time-zoom selector (24h · 48h · 7g · 30g · 90g · Tutto), the responsive
 * {@link HistoryChart}, a textual legend, and a screen-reader summary of each
 * triage line. Handles the loading / error / empty states so the page stays
 * declarative.
 *
 * One selector spans two data shapes: the short steps read the 5-minute history
 * (fine detail, gaps carried forward); the long steps read the hourly-aggregate
 * trend (coarser buckets, honest gaps, a min/max spread band). The page picks
 * the matching query and hands the already-built {@link ChartSeries} down here,
 * so this component stays presentational and shape-agnostic.
 *
 * Colour is never the only channel: the legend names each line and a
 * visually-hidden table states each triage's current/min/max for the window, so
 * the trend is legible to screen-reader and colour-blind users (WCAG 2.1 AA).
 *
 * The legend entries are real toggle `<button>`s (keyboard-operable, with
 * `aria-pressed`). Click semantics are a plain per-series toggle: pressed = the
 * line is shown. So you *isolate/solo* one line by muting the others, and *mute
 * several* by clicking each off — one predictable rule covers both. Muting the
 * last line that still has data is refused, so the chart never collapses to an
 * empty frame; a series with no data at all is a disabled button (label kept).
 * Muting rescales the y-axis to the *visible* peak (rescale-to-visible, not a
 * fixed axis) so a soloed small line fills the chart — always still anchored at
 * 0, and the sr-only summary and the axis both track what's actually visible.
 * State is ephemeral local UI: it resets when the series set changes (a zoom
 * step with different triage levels) and is never written to the URL (#180).
 */

import { useState } from "react";
import { ParentSize } from "@visx/responsive";
import { HistoryChart } from "./HistoryChart";
import {
  hasAnySamples,
  hasSamples,
  seriesSummary,
  toggleSeriesVisibility,
  ZOOM_OPTIONS,
  type ChartSeries,
  type TimeZoom,
} from "@/lib/history";
import { formatRomeClock, formatRomeDayMonth } from "@/lib/time";

const EMPTY_HIDDEN: ReadonlySet<number> = new Set();

const CHART_HEIGHT = 260;

interface HistoryChartCardProps {
  /** Chart series for the active zoom, already built from the matching response. */
  series: ChartSeries[];
  isPending: boolean;
  isError: boolean;
  onRetry: () => void;
  zoom: TimeZoom;
  onZoomChange: (zoom: TimeZoom) => void;
}

export function HistoryChartCard({
  series,
  isPending,
  isError,
  onRetry,
  zoom,
  onZoomChange,
}: HistoryChartCardProps) {
  const showChart = !isPending && !isError && hasAnySamples(series);
  // Intraday windows read as wall-clock ticks; multi-day windows as dates.
  const xTickFormat = zoom.kind === "history" ? formatRomeClock : formatRomeDayMonth;

  // Ephemeral legend mute/solo state, keyed on the current set of triage codes:
  // when the codes change (a zoom step exposing different levels) the stored set
  // no longer applies, so we fall back to "nothing muted" without an effect.
  const codesKey = series.map((s) => s.triageCode).join(",");
  const [muteState, setMuteState] = useState<{ key: string; hidden: ReadonlySet<number> }>({
    key: codesKey,
    hidden: EMPTY_HIDDEN,
  });
  const hidden = muteState.key === codesKey ? muteState.hidden : EMPTY_HIDDEN;
  const toggleSeries = (code: number) =>
    setMuteState({ key: codesKey, hidden: toggleSeriesVisibility(series, hidden, code) });

  return (
    <section className="detail-panel" aria-labelledby="history-title" aria-busy={isPending}>
      <header className="detail-panel__head">
        <h2 id="history-title" className="detail-panel__title">
          Andamento coda
        </h2>
        <ZoomSelector value={zoom} onChange={onZoomChange} />
      </header>

      {isPending && <div className="history-chart__canvas history-chart__canvas--skeleton" />}

      {isError && !isPending && (
        <div className="detail-panel__error" role="alert">
          <p>Impossibile caricare lo storico.</p>
          <button type="button" className="detail-panel__retry" onClick={onRetry}>
            Riprova
          </button>
        </div>
      )}

      {!isPending && !isError && !showChart && (
        <p className="detail-panel__empty">
          Nessuno storico disponibile per questa finestra — i dati si accumulano nel tempo.
        </p>
      )}

      {showChart && (
        <>
          <div className="history-chart__canvas">
            <ParentSize debounceTime={0}>
              {({ width }) => (
                <HistoryChart
                  series={series}
                  width={width}
                  height={CHART_HEIGHT}
                  xTickFormat={xTickFormat}
                  hidden={hidden}
                  ariaLabel={`Andamento delle persone in attesa per livello di triage ${zoom.summary}`}
                />
              )}
            </ParentSize>
          </div>
          <ChartLegend series={series} hidden={hidden} onToggle={toggleSeries} />
          <SeriesSummaryTable
            series={series.filter((s) => !hidden.has(s.triageCode))}
            zoom={zoom}
          />
        </>
      )}
    </section>
  );
}

function ZoomSelector({
  value,
  onChange,
}: {
  value: TimeZoom;
  onChange: (zoom: TimeZoom) => void;
}) {
  return (
    <div className="window-selector" role="group" aria-label="Finestra temporale">
      {ZOOM_OPTIONS.map((option) => (
        <button
          key={option.id}
          type="button"
          className="window-selector__btn"
          aria-pressed={option.id === value.id}
          onClick={() => onChange(option)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function ChartLegend({
  series,
  hidden,
  onToggle,
}: {
  series: ChartSeries[];
  hidden: ReadonlySet<number>;
  onToggle: (code: number) => void;
}) {
  return (
    <ul className="chart-legend">
      {series.map((s) => {
        const hasData = hasSamples(s);
        const shown = !hidden.has(s.triageCode);
        return (
          <li key={s.triageCode} className="chart-legend__item">
            <button
              type="button"
              className="chart-legend__btn"
              // Pressed = the line is shown. Disabled when the series has no data
              // in this window (label stays, so colour is never the only channel).
              aria-pressed={hasData ? shown : undefined}
              disabled={!hasData}
              data-muted={hasData && !shown ? "1" : undefined}
              onClick={() => onToggle(s.triageCode)}
            >
              <span
                className="chart-legend__swatch"
                style={{ backgroundColor: s.color }}
                aria-hidden="true"
              />
              <span className="chart-legend__label">{s.label}</span>
              {!hasData && <span className="chart-legend__note"> (nessun dato)</span>}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

/** Visually-hidden equivalent of the chart for assistive tech. */
function SeriesSummaryTable({ series, zoom }: { series: ChartSeries[]; zoom: TimeZoom }) {
  return (
    // Visually-hidden accessible companion to the chart. The wrapper does the
    // hiding/clipping: a `.sr-only` table can't shrink below its caption's
    // min-content width, so hiding the table directly would push the page into
    // horizontal scroll on mobile (issue #45).
    <div className="sr-only">
      <table>
        <caption>
          Persone in attesa per livello di triage {zoom.summary} (valore attuale, minimo e massimo).
        </caption>
        <thead>
          <tr>
            <th scope="col">Triage</th>
            <th scope="col">Attuale</th>
            <th scope="col">Min</th>
            <th scope="col">Max</th>
          </tr>
        </thead>
        <tbody>
          {series.map((s) => {
            const summary = seriesSummary(s);
            return (
              <tr key={s.triageCode}>
                <th scope="row">{s.label}</th>
                <td>{summary ? summary.current : "—"}</td>
                <td>{summary ? summary.min : "—"}</td>
                <td>{summary ? summary.max : "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
